import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, debounceTime, filter, map, merge, mergeMap, Observable, Subscription, take, tap } from 'rxjs';
import moment from 'moment-timezone';

import { DropdownOptionModel, OptionModel} from '../../../../Common/Forms/models/dropdown-option.model';

import { MonitoringDashboardSearchService } from '../../../Core/services/monitoring-dashboard-search.service';
import { MonitoringAnalyticsDashboardService } from '@Monitor/Shared/services/monitoring-analytics-dashboard.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { SearchRangeModel } from '@Monitor/Core/models/search-range.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { RouteType } from '@Monitor/Shared/enums/route-type.enum';
import { MonitoringViewModesEnum } from '../../enums/monitoring-view-modes.enum';
import { MonitoringRouteModel } from '@Monitor/Shared/models/monitoring-route.model';
import { SearchResultsService } from '@Monitor/Core/services/search-results.service';
import { FilterChipsComponent } from '../../../../Common/Filter/components/filterChips/filter-chips.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { TopicsService } from '@Monitor/Core/services/topics.service';
import { FolderService } from '@Monitor/Core/services/folder.service';
import { MediaSubscriptionService } from '@Monitor/Core/services/media-subscription.service';
import { SearchErrorsEnum } from '@Monitor/Core/enums/search-errors.enum';
import { SearchErrorTypeEnum } from '../MonitoringResult/mentionSearchResult/mention-search-result.component';
import { InvalidFiltersError } from '@Monitor/Shared/errors/invalid-filters.error';
import { SearchStatesEnum } from '@Monitor/Shared/enums/search-states.enum';
import { SearchStateErrorModel } from '@Monitor/Core/models/search-state-error.model';
import { SearchError } from '@Monitor/Core/errors/search.error';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { SaveTopicDialogComponent } from '../SaveTopicDialog/save-topic-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { MediaSubscriptionMediaTypeEnum, mediaSubTypeToMediaTypeMap } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { ReportDateRangeModel } from '../../../../Common/Reports/models/report-date-range.model';


@Component({
    selector: 'agpr-monitoring-analytics-dashboard',
    templateUrl: './monitoring-analytics-dashboard.component.html',
    styleUrls: ['./monitoring-analytics-dashboard.component.scss'],
    standalone: false
})
export class MonitoringAnalyticsDashboardComponent implements OnInit, OnDestroy {

    currentRoute: MonitoringRouteModel;
    viewModeOptions = [
        new OptionModel('Monitor.MAD.viewToggle.mention', MonitoringViewModesEnum.MENTION),
        new OptionModel('Monitor.MAD.viewToggle.syndicate', MonitoringViewModesEnum.SYNDICATE),
        new OptionModel('Monitor.MAD.viewToggle.dashboard', MonitoringViewModesEnum.DASHBOARD)
    ];
    selectedViewMode: MonitoringViewModesEnum;
    topicSearchRange: SearchRangeModel;
    searchRange: SearchRangeModel;
    startDateRange: DateQueryModel;
    hasInvalidDateRange: boolean;
    validDateRangeStartDisplayString: string;
    validDateRangeEndDisplayString: string;
    filtersLocked: boolean = false;
    selectedTabIndex: number = 0;
    seriesMap: Map<any, ReportSeriesModel> = null;
    errorType: SearchErrorTypeEnum;
    filterError: InvalidFiltersError;
    search: MentionSearchModel;
    topicDirty: boolean = false;
    /**
     * The heading and the Analytics/Mention Feed toggle. False where the surrounding page already
     * says which view this is - the agent dashboard, whose left hand panel carries that navigation.
     */
    @Input() showHeader: boolean = true;

    isAccountSearch: boolean = false;
    isSavedCoverage: boolean = false;
    saveInProgress: boolean = false;
    mediaSubscription: MediaSubscription;
    enableActiveButton: boolean;
    activateButtonTooltip: string;
    isTvEyesEnabled: boolean = false;
    availableMediaTypes: MediaTypeEnum[];
    initialFulfillSeriesCooldown: boolean = true;

    subscriptions: Subscription[] = [];
    pageHeading$: Observable<string>;
    pageSRHeading$: Observable<string>;
    dateFilterOptions$: Observable<DropdownOptionModel[]>;
    dateTypeFilterOptions$: Observable<DropdownOptionModel[]>;
    refreshing$: Observable<boolean>;
    private seriesMapReady$: BehaviorSubject<Map<any, ReportSeriesModel>> = new BehaviorSubject<Map<any, ReportSeriesModel>>(null);
    private mentionSearchFilterReady: boolean = false;
    private mentionSearchFilterReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private dashboardRouteInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly buttonTypes = ButtonTypesEnum;
    readonly routeTypes = RouteType;
    readonly errorTypes = SearchErrorTypeEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly saveActions = [
        {
            label: this.localizationService.transform('Monitoring.search.saveChanges'),
            command: () => this.saveTopicChanges()
        },
        {
            label: this.localizationService.transform('Monitoring.saveAsNewTopic'),
            command: () => this.saveAsNewTopic()
        }
    ];
    readonly mediaSubscriptionStatus = MediaSubscriptionStatusEnum;

    @ViewChild('filterChipsComponent') filterChipsComponent: FilterChipsComponent;

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        public routingService: MonitoringRoutingService,
        public dashboardService: MonitoringAnalyticsDashboardService,
        public dashboardSearchService: MonitoringDashboardSearchService,
        public searchResultsService: SearchResultsService,
        public topicsService: TopicsService,
        public folderService: FolderService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public mediaSubscriptionService: MediaSubscriptionService
    ) {
        this.pageHeading$ = this.dashboardService.getPageHeading();
        this.pageSRHeading$ = this.dashboardService.getPageSRHeading();
        this.dateFilterOptions$ = this.dashboardService.getDateFilterOptions();
        this.dateTypeFilterOptions$ = this.dashboardService.getDateTypeFilterOptions();
        this.refreshing$ = this.dashboardSearchService.getIsFetchData();

        const loadSearch = merge(topicsService.currentResource$, folderService.currentResource$).subscribe(x => {
            if (x instanceof MentionSearchModel) {
                this.search = x;
                this.isAccountSearch = false;
                if (x.searchCriteria) {
                    this.isAccountSearch = x.searchCriteria.isAccountSearch;
                }
            }
        });
        const loadMediaSubscription = this.mediaSubscriptionService.currentResource$.pipe(filter(x => !!x)).subscribe(x => {
            this.mediaSubscription = x;
            this.enableActiveButton = !!this.availableMediaTypes?.
                find(type => type === mediaSubTypeToMediaTypeMap.get(this.mediaSubscription.type));
            this.activateButtonTooltip = (this.enableActiveButton) ? '' : 'MediaSubscriptions.activate.disabled';
        });
        const loadTvEyesConfig = this.monitorConfigService.getTvEyesEnabled().subscribe(enabled => {
            this.isTvEyesEnabled = enabled;
            this.activateButtonTooltip = (this.enableActiveButton) ? '' : 'MediaSubscriptions.activate.disabled';
        });
        const searchCriteriaInitialized$ = this.dashboardRouteInitialized$.pipe(
            filter(x => !!x),
            mergeMap(() => {
                return this.dashboardSearchService.getSearchCriteria().pipe(filter(x => {
                    if (!x) {
                        return false;
                    }
                    switch (this.currentRoute.type) {
                        case RouteType.folderAnalyticsDashboard:
                        case RouteType.folderAnalyticsSyndicates:
                            return x.query.folder.include.length > 0;
                        case RouteType.allSavedCoverageAnalyticsDashboard:
                        case RouteType.topicAnalyticsDashboard:
                        case RouteType.subscriptionAnalyticsDashboard:
                        case RouteType.adHocAnalyticsDashboard:
                        case RouteType.allSavedCoverageAnalyticsSyndicates:
                        case RouteType.topicAnalyticsSyndicates:
                        case RouteType.subscriptionAnalyticsSyndicates:
                        case RouteType.adHocAnalyticsSyndicates:
                            return !x.equals(new MentionSearchCriteriaModel());
                        default:
                            return false;
                    }
                }));
            }),
            take(1)
        );
        const filterValidation = this.dashboardSearchService.getSearchState().subscribe(
            searchState => {
                this.filterError = null;
                this.errorType = SearchErrorTypeEnum.none;

                switch (searchState.type) {
                    case SearchStatesEnum.loading:
                        // this.isLoadingMentions = true;
                        break;
                    case SearchStatesEnum.error:
                        const errorState = searchState as SearchStateErrorModel;
                        if (errorState.error instanceof InvalidFiltersError) {
                            this.errorType = SearchErrorTypeEnum.filters;
                            this.filterError = errorState.error;
                        } else if (errorState.error instanceof SearchError) {
                            switch (errorState.error.type) {
                                case SearchErrorsEnum.queryTooLong:
                                    this.errorType = SearchErrorTypeEnum.queryLength;
                                    break;
                                case SearchErrorsEnum.discoverListNotExist:
                                    this.errorType = SearchErrorTypeEnum.discoverListNotExist;
                                    break;
                                case SearchErrorsEnum.unknown:
                                default:
                                    this.errorType = SearchErrorTypeEnum.unknown;
                                    break;
                            }
                        } else {
                            this.errorType = SearchErrorTypeEnum.unknown;
                        }
                        break;
                    default:
                }
            }
        );
        const filterChange = this.dashboardSearchService.getFilters().pipe(debounceTime(300)).subscribe(filters => {
            if (this.mentionSearchFilterReady && !this.initialFulfillSeriesCooldown) {
                this.dashboardSearchService.fetchDataForSeries(true);
            } else {
                this.mentionSearchFilterReady = true;
                this.mentionSearchFilterReady$.next(this.mentionSearchFilterReady);
            }
        });
        const validateDateRange = combineLatest([
            this.routingService.getCurrentRoute().pipe(
                tap(x => {
                    this.currentRoute = x;
                    this.updateMentionViewMode();
                    if (
                        x.type === RouteType.topicAnalyticsDashboard
                        || x.type === RouteType.folderAnalyticsDashboard
                        || x.type === RouteType.allSavedCoverageAnalyticsDashboard
                        || x.type === RouteType.subscriptionAnalyticsDashboard
                        || x.type === RouteType.adHocAnalyticsDashboard
                    ) {
                        this.dashboardRouteInitialized$.next(true);
                    }

                    if (x.type === RouteType.folderAnalyticsDashboard ||
                        x.type === RouteType.folderAnalyticsSyndicates ||
                        x.type === RouteType.allSavedCoverageAnalyticsDashboard ||
                        x.type === RouteType.allSavedCoverageAnalyticsSyndicates) {
                        this.isSavedCoverage = true;
                    } else {
                        this.isSavedCoverage = false;
                    }

                    if (x.type === RouteType.results && this.search && this.search.searchCriteria
                        && this.search.searchCriteria.isAccountSearch) {
                        this.isAccountSearch = true;
                    }
                }),
                filter(x => !!x)
            ),
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x),
                map(pref => pref.timeZone)
            ),
            this.dashboardSearchService.getDateQuery().pipe(
                tap(dateQuery => {
                    this.startDateRange = dateQuery;
                    this.seriesMap?.forEach(series => series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.startDateRange));
                })
            ),
            this.monitorConfigService.getSearchRange().pipe(
                tap(searchRange => this.topicSearchRange = searchRange),
                filter(x => !!x)
            )
        ]).subscribe(([route, userPreferenceTimezone, startDateRange, searchRange]) => {
            // if user switches from saved coverage search all date range to a topic, we will display an out of date range message
            this.hasInvalidDateRange = startDateRange.absoluteRange && startDateRange.absoluteRange.start < searchRange.from
                && startDateRange.absoluteRange.end > searchRange.from
                && route.type === RouteType.topic;
            if (this.hasInvalidDateRange) {
                this.validDateRangeStartDisplayString = moment(searchRange.from).tz(userPreferenceTimezone).format('YYYY/MM/DD');
                this.validDateRangeEndDisplayString = moment(startDateRange.absoluteRange.end).tz(userPreferenceTimezone).format('YYYY/MM/DD');
            }
            // determine if the search range should take effect
            this.searchRange = this.dashboardService.isUnsavedMentionRoute() ? this.topicSearchRange : null;
        });

        this.subscriptions.push(
            loadSearch,
            loadMediaSubscription,
            loadTvEyesConfig,
            filterValidation,
            filterChange,
            validateDateRange,
            combineLatest([
                searchCriteriaInitialized$,
                this.seriesMapReady$.pipe(filter(x => !!x)),
                this.mentionSearchFilterReady$.pipe(
                    filter(x => !!x),
                    take(1)
                )
            ]).subscribe(([searchCriteria, seriesMap, filterReady]) => {
                this.seriesMap = seriesMap;
                this.seriesMap.forEach(series => series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.startDateRange));

                this.dashboardSearchService.fulfillSeries(Array.from(this.seriesMap.values()));
                /**
                 * Note: setFilters are called in a lot of places, so this getFilters will be called many times,
                 * even after the initial fulfillSeries, and it is why we see fetchData twice on load.
                 * Therefore, we set a timed flag here to avoid the second fetch.
                 * Assuming user would not actually update filters in this period
                 */
                setTimeout(() => this.initialFulfillSeriesCooldown = false, 1000);
            }),
            this.topicsService.getDirtyFlag().subscribe(isDirty => this.topicDirty = isDirty),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(availableMediaTypes => this.availableMediaTypes = availableMediaTypes)
        );
    }

    ngOnInit(): void {
        this.filtersLocked = this.searchResultsService.filtersLocked();
        this.searchResultsService.setIsViewModeChange(false);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
        if (!this.searchResultsService.filtersLocked() && !this.searchResultsService.getIsViewModeChange()) {
            this.clearFilters();
        }
    }

    //#region toolbar functionality

    updateMentionViewMode(): void {
        switch (this.currentRoute.type) {
            case RouteType.topicAnalyticsDashboard:
            case RouteType.adHocAnalyticsDashboard:
            case RouteType.folderAnalyticsDashboard:
            case RouteType.allSavedCoverageAnalyticsDashboard:
            case RouteType.subscriptionAnalyticsDashboard:
                this.selectedViewMode = MonitoringViewModesEnum.DASHBOARD;
                break;
            case RouteType.topicAnalyticsSyndicates:
            case RouteType.adHocAnalyticsSyndicates:
            case RouteType.folderAnalyticsSyndicates:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
            case RouteType.subscriptionAnalyticsSyndicates:
                this.selectedViewMode = MonitoringViewModesEnum.SYNDICATE;
                break;
            default:
                this.selectedViewMode = MonitoringViewModesEnum.MENTION;
        }
    }

    viewModeChanged(mode: any): void {
        if (mode === this.selectedViewMode) {
            return;
        }
        this.searchResultsService.setCurrentViewMode(mode);
        this.searchResultsService.setIsViewModeChange(true);

        this.routingService.handleViewModeRouteChange(mode, this.currentRoute);
    }

    dateRangeChanged(dateRange: DateQueryModel): void {
        this.dashboardSearchService.setDateQuery(dateRange);
        this.dashboardSearchService.search(true);
        this.dashboardSearchService.fetchDataForSeries(true);
    }

    clearFilters(doSearch: boolean = false): void {
        this.filterChipsComponent?.chipContainer.chipPopover.hide();
        this.dashboardSearchService.clearFilters();
        this.filtersLocked = false;
        this.searchResultsService.setFiltersLocked(false);

        if (doSearch) {
            // refresh primary facets as well because this also resets the date query
            this.dashboardSearchService.search(true, true);
        }
    }

    toggleFilterLock() {
        this.filtersLocked = !this.filtersLocked;
        this.searchResultsService.setFiltersLocked(this.filtersLocked);
    }

    //#endregion

    fulfillSeries(seriesMap: Map<any, ReportSeriesModel>): void {
        this.dashboardSearchService.deregisterSeries();
        this.seriesMapReady$.next(seriesMap);
    }

    updateSeries(series: { oldSeriesTypes: any[]; newSeries: ReportSeriesModel[] }): void {
        const replacedSeries = [];
        series.oldSeriesTypes.forEach((x, i) => {
            const oldSeries = this.seriesMap.get(x);
            if (oldSeries) {
                replacedSeries.push(oldSeries);
                // assumption here is that oldSeriesTypes and newSeries have the same length
                this.seriesMap.set(x, series.newSeries[i]);
            }
        });
        this.dashboardSearchService.deregisterSeries(replacedSeries);
        this.dashboardSearchService.fulfillSeries(series.newSeries);
    }

    searchNewContent() {
        if (!this.search.searchCriteria.query.isUnsavedSearchValid()) {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.heading',
                    message: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.text',
                    okLabel: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.updateSearch',
                    id: 'InvalidNewSearchPopup'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.convertToUnsavedSearch();
                            this.routingService.gotoUpdateSearch();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            this.convertToUnsavedSearch();
            this.dashboardSearchService.setSearchCriteria(this.search.searchCriteria);
            this.dashboardService.setActiveDateFilterOptions();
            this.dashboardSearchService.clearSearch();

            this.dateRangeChanged(this.startDateRange);
        }
    }

    convertToUnsavedSearch(): void {
        this.search.searchCriteria.isAccountSearch = false;
        this.search.searchCriteria.query.includedMediaTypes = this.search.searchCriteria.query.includedMediaTypes.filter(media => {
            return !media.isSavedSearch;
        });
        if (
            DateQueryModel.equals(this.startDateRange, DateQueryModel.getAllQuery()) ||
            (
                this.startDateRange.type === DateQueryTypeEnum.ABSOLUTE &&
                this.startDateRange.absoluteRange.start <= this.searchRange.from
            )
        ) {
            this.startDateRange = DateQueryModel.getLast24HoursQuery();
            this.hasInvalidDateRange = false;
            this.validDateRangeStartDisplayString = '';
            this.validDateRangeEndDisplayString = '';
        }

        this.search.searchCriteria.query.folder.include = [];
        this.search.searchCriteria.query.folder.exclude = [];
        this.search.searchCriteria.query.tag.include = [];
        this.search.searchCriteria.query.tag.exclude = [];

        this.isAccountSearch = false;
    }

    /**
     * Routes to the update search workflow
     */
    updateSearch(): void {
        this.topicsService.openEditTopicPanel(this.search);
    }

    /**
     * Goes to the update workflow for the current search
     */
    updateSavedSearch(): void {
        this.topicsService.openEditTopicPanel(this.search);
    }

    refresh(): void {
        this.dashboardSearchService.fetchDataForSeries();
    }

    gotoUpdateSubscription(): void {
        this.routingService.gotoUpdateMediaSubscription(+this.currentRoute.value);
    }

    /**
     * Saves changes made to a topic
     */
    public saveTopicChanges() {
        if (this.saveInProgress) {
            return;
        }

        this.saveInProgress = true;
        this.topicsService.updateTopic(this.search).subscribe(
            () => {
                this.saveInProgress = false;
            }
        );
    }

    saveAsNewTopic(): void {
        this.coreDialogService.open(
            SaveTopicDialogComponent,
            {
                inputs: {
                    search: this.search,
                    saveAsNew: true,
                    currentGroup: this.search.groupId
                }
            }
        );
    }

    saveTopic(): void {
        this.coreDialogService.open(
            SaveTopicDialogComponent,
            {
                inputs: {
                    search: this.search,
                    saveAsNew: false
                }
            }
        );
    }

    activate() {
        this.mediaSubscriptionService.activateCurrentSubscription().subscribe();
    }

    pause() {
        this.mediaSubscriptionService.pauseCurrentSubscription().subscribe();
    }
}
